import React, { useEffect, useState, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiFolderPlus, FiUpload } from "react-icons/fi";
import { FaHome } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import { Spinner } from "../../Components/Common";
import { MESSAGES } from "./constants";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { FolderView, UserAccessModal } from "./Components";
import { UserContext } from "../../Context";
import { getAllUsers } from "../../Services/userService";
import * as folderServices from "../../Services/folderServices";
import { FOLDER_TYPES } from "./constants";

const FolderRoot = () => {
  const { type } = useParams();
  const isMyDrive = type === FOLDER_TYPES.MY;
  const isSharedDrive = type === FOLDER_TYPES.SHARED;
  const { user: contextUser } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [typeLoading, setTypeLoading] = useState(true);

  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderStack, setFolderStack] = useState([]);

  const fileInputRef = useRef(null);

  const [usersList, setUsersList] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalTargetFolder, setModalTargetFolder] = useState(null);

  const loadDrive = async () => {
    setTypeLoading(true);

    try {
      if (isMyDrive) {
        const response = await folderServices.getDriveRoot(FOLDER_TYPES.MY);
        const root = response.data.folders?.[0];

        if (!root) throw new Error("My Drive root missing");

        setCurrentFolder(root);
        setFolderStack([{ id: root.id, name: "My Drive" }]);
      } else {
        const response = await folderServices.listDrive(FOLDER_TYPES.SHARED);
        const sharedFolders = response.data.folders || [];

        const root = {
          id: null,
          name: "Shared",
          subfolders: sharedFolders,
          files: [],
        };

        setCurrentFolder(root);
        setFolderStack([{ id: null, name: "Shared" }]);
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err) ||
          (isMyDrive
            ? MESSAGES.FAILED_LOAD_MY_DRIVE
            : MESSAGES.FAILED_LOAD_SHARED_DRIVE)
      );
    } finally {
      setLoading(false);
      setTypeLoading(false);
    }
  };

  const getFolderById = async (folderId) => {
    setLoading(true);
    try {
      const response = await folderServices.getFolderListById(folderId);
      return response.data;
    } catch (err) {
      toast.error(MESSAGES.FOLDERS_LOADING_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = prompt("Enter folder name:");
    if (!name || !currentFolder?.id) return;

    try {
      await folderServices.createFolder({
        name,
        parent: currentFolder.id,
      });
      toast.success(MESSAGES.FOLDER_CREATED);
    } catch {
      toast.error(MESSAGES.FOLDER_CREATION_FAILED);
    } finally {
      const updatedFolder = await getFolderById(currentFolder.id);
      setCurrentFolder(updatedFolder);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);

    const data = new FormData();
    data.append("file", file);
    data.append("folder", currentFolder.id);

    try {
      await folderServices.uploadFile(data);
      toast.success(MESSAGES.FILE_UPLOADED);
    } catch {
      toast.error(MESSAGES.FILE_UPLOAD_FAILED);
    } finally {
      const updated = await getFolderById(currentFolder.id);
      setCurrentFolder(updated);
      setLoading(false);
    }
  };

  const openFolder = async (folder) => {
    const updated = await getFolderById(folder.id);

    setFolderStack((prev) => [...prev, { id: updated.id, name: updated.name }]);

    setCurrentFolder(updated);
  };

  const renderBreadcrumb = () => {
    if (!folderStack.length) return null;

    const MAX_VISIBLE = 4;
    const root = folderStack[0];

    const trail = folderStack.slice(1);

    let visibleTrail = trail;
    let showEllipsis = false;

    if (trail.length > MAX_VISIBLE) {
      showEllipsis = true;
      visibleTrail = trail.slice(trail.length - MAX_VISIBLE);
    }

    return (
      <div className="flex items-center gap-1 text-sm text-gray-700 overflow-x-auto max-w-full">
        <span
          className="cursor-pointer text-primary hover:text-primary/80 flex items-center"
          onClick={() => goBackTo(0, root.id)}
        >
          <FaHome size={16} className="mr-1" /> Home
        </span>

        {trail.length > 0 && (
          <FiChevronRight size={14} className="text-gray-500" />
        )}

        {showEllipsis && (
          <>
            <span className="text-gray-500 cursor-default">...</span>
            <FiChevronRight size={14} className="text-gray-500" />
          </>
        )}

        {visibleTrail.map((folder, idx) => {
          const stackIndex = folderStack.indexOf(folder);

          return (
            <React.Fragment key={folder.id || idx}>
              <span
                className="cursor-pointer text-primary hover:underline whitespace-nowrap"
                onClick={() => goBackTo(stackIndex, folder.id)}
              >
                {folder.name}
              </span>

              {idx < visibleTrail.length - 1 && (
                <FiChevronRight size={14} className="text-gray-500" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const goBackTo = async (index, folderId) => {
    if (folderId === null) {
      loadDrive();
      return;
    }

    const newStack = folderStack.slice(0, index + 1);
    const target = await getFolderById(folderId);

    setFolderStack(newStack);
    setCurrentFolder(target);
  };

  const handleDeleteItem = async (type, obj) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ${type}?`
    );
    if (!confirmDelete) return;

    setLoading(true);

    try {
      if (type === "file") await folderServices.deleteFile(obj.id);
      else await folderServices.deleteFolder(obj.id);

      toast.success(`${type} deleted`);
    } catch {
      toast.error(`Failed to delete ${type}`);
    } finally {
      loadDrive();
      setLoading(false);
    }
  };

  const fetchUsers = async (folder) => {
    try {
      const response = await getAllUsers({ limit: 0 });
      const allUsers = response.data.results || response.data;

      setUsersList(allUsers);
      setModalTargetFolder(folder);
      setShowUserModal(true);
    } catch {
      toast.error(MESSAGES.USER_FETCH_FAIL);
    }
  };

  const openAccessModal = (folder) => {
    fetchUsers(folder);
  };

  const handleSaveAccess = async (selectedUsers) => {
    try {
      await folderServices.addUserToFolder(modalTargetFolder.id, selectedUsers);

      const updated = await getFolderById(currentFolder.id);
      setCurrentFolder(updated);

      toast.success(MESSAGES.FOLDER_ACCESS_ADDED);
    } catch {
      toast.error(MESSAGES.FOLDER_ACCESS_ADD_FAIL);
    } finally {
      setShowUserModal(false);
    }
  };

  useEffect(() => {
    loadDrive();
  }, [type]);

  if (typeLoading)
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Spinner />
      </div>
    );

  return (
    <div>
      <div className="flex justify-between items-center px-4 mt-4">
        <h1 className="text-xl font-semibold text-primary">
          {isMyDrive ? "My Drive" : "Shared Drive"}
        </h1>

        {currentFolder && 
          (isMyDrive || (isSharedDrive && currentFolder.id !== null)) && (
          <div className="flex gap-2">
            <button
              onClick={handleCreateFolder}
              disabled={loading}
              className="px-3 py-1 bg-primary text-white rounded"
              title="Add Folder"
            >
              <FiFolderPlus size={20} />
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="px-3 py-1 bg-primary text-white rounded"
              title="Upload file"
            >
              <FiUpload size={20} />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        )}
      </div>

      <div className="px-4 py-2">{renderBreadcrumb()}</div>

      <div className="h-[70vh] overflow-y-scroll px-2">
        {currentFolder &&
        currentFolder.subfolders?.length === 0 &&
        currentFolder.files?.length === 0 ? (
          <div className="text-center text-gray-500 mt-20 text-sm">
            {isMyDrive
              ? "Your drive is empty. Create a folder or upload files."
              : "No shared folders or files available."}
          </div>
        ) : (
          <FolderView
            currentFolder={currentFolder}
            openFolder={openFolder}
            handleDeleteItem={handleDeleteItem}
            folderType={type}
            openAccessModal={openAccessModal}
          />
        )}
      </div>

      {showUserModal && (
        <UserAccessModal
          folder={modalTargetFolder}
          usersList={usersList}
          onClose={() => setShowUserModal(false)}
          onSave={handleSaveAccess}
        />
      )}
    </div>
  );
};

export default FolderRoot;
